import {useDeviceDraft} from '../lib/useDeviceDraft';
import {offlineState} from '../lib/offlineVault';
import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instructionsApi } from '../lib/api';
import { decodeClinicalNote, encodeClinicalNote, noteMatches, NOTE_SECTIONS, type NoteSection } from '../lib/clinicalNotes';

export function ClinicalNotesPanel({ patientId, encounterId, section, resourceId, title = 'Additional notes' }: {
    patientId: string;
    encounterId?: string;
    section?: NoteSection;
    resourceId?: string;
    title?: string;
}) {
    const uid = useId();
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useDeviceDraft(`note:${patientId}:${encounterId??''}:${section??''}:${resourceId??''}`, '');
    const [requestId,setRequestId]=useDeviceDraft(`note-key:${patientId}:${encounterId??''}:${section??''}:${resourceId??''}`,()=>crypto.randomUUID());
    const [category, setCategory] = useDeviceDraft<NoteSection>(`note-category:${patientId}:${encounterId??''}:${section??''}:${resourceId??''}`,section ?? 'general');
    const [replyTo, setReplyTo] = useDeviceDraft<string | null>(`note-reply:${patientId}:${encounterId??''}:${section??''}:${resourceId??''}`,null);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const list = useQuery({
        queryKey: ['patient-instructions', patientId, true],
        queryFn: () => instructionsApi.list(patientId, { includeResolved: true }),
    });
    const entries = (list.data?.instructions ?? []).flatMap(item => {
        const note = decodeClinicalNote(item.body);
        if (!note || !noteMatches(note, { encounterId, section, resourceId })) return [];
        return [{ item, note }];
    }).sort((a, b) => a.item.created_at.localeCompare(b.item.created_at));
    const context = { encounter: encounterId ?? null, section: category, resource: resourceId ?? null, replyTo };
    const encoded = encodeClinicalNote(context, draft);
    const save = useMutation({
        mutationFn: () => instructionsApi.create(patientId, { body: encoded,clientRequestId:requestId }),
        onSuccess: async () => {
            setDraft('');setRequestId(crypto.randomUUID()); setReplyTo(null); setOpen(false); setError(''); setSaved(true);
            await qc.invalidateQueries({ queryKey: ['patient-instructions', patientId] });
        },
        onError: () => setError('Could not save this note. Your text is retained. Check your connection and permissions before retrying.'),
        retry: false,
    });
    function start(parent: string | null = null, selected = section ?? 'general') {
        if(offlineState()?.operations.some(o=>o.source_draft_id===requestId)){setDraft('');setRequestId(crypto.randomUUID());}
        setReplyTo(parent); setCategory(selected); setError(''); setSaved(false); setOpen(true);
    }
    return <section className="rounded-lg border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium text-slate-900">{title}</h3>
            <button type="button" className="pm-btn-outline text-xs" disabled={save.isPending}
                onClick={() => start()}>+ Add new note</button>
        </div>
        <p className="text-xs text-slate-500">Add a clarification or observation without replacing the original. Author and time are recorded when saved.</p>
        {list.isLoading && <p role="status">Loading notes…</p>}
        {list.isError && <div role="alert">Could not load note history. <button type="button" onClick={() => void list.refetch()}>Retry</button></div>}
        {saved && <p role="status" className="text-sm text-emerald-700">{offlineState()?'Note saved on this device. Check connection status for server confirmation.':'Note saved.'}</p>}
        {entries.map(({ item, note }) => <article key={item.id} className="border-l-2 border-brand-200 pl-3 space-y-1">
            <div className="text-xs text-slate-500">
                {NOTE_SECTIONS[note.section]} · {item.author_full_name || ('Staff member ' + (item.author_clinician_id ?? 'unavailable'))}
                {' · '}<time dateTime={item.created_at} title={item.created_at}>{new Date(item.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'long' })}</time>
            </div>
            {note.replyTo && <blockquote className="text-xs text-slate-500 border-l pl-2">
                Clarification of: {entries.find(entry => entry.item.id === note.replyTo)?.note.text.slice(0, 160) ?? 'an earlier note (reference ' + note.replyTo + ')'}
            </blockquote>}
            {!encounterId && note.encounter && <p className="text-xs text-slate-500">Encounter: {note.encounter}</p>}
            <p className="text-sm whitespace-pre-wrap break-words">{note.text}</p>
            <button type="button" className="text-brand-700 text-xs underline" disabled={save.isPending}
                onClick={() => start(item.id, note.section)}>+ Add clarification</button>
        </article>)}
        {list.data && entries.length === 0 && <p className="text-xs text-slate-500">No additional notes yet.</p>}
        {(list.data?.instructions.length ?? 0) >= 200 && <p role="status" className="text-xs text-amber-700">The service returns the latest 200 patient notes. Older history may not appear here.</p>}
        {open && <form className="space-y-2" onSubmit={e => {
            e.preventDefault();
            if (!draft.trim() || encoded.length > 2000 || save.isPending) return;
            if (draft.includes('@')) { setError('Mentions are disabled during internal testing. Remove @ before saving.'); return; }
            save.mutate();
        }}>
            {!section && !replyTo && <label className="pm-label" htmlFor={uid + '-category'}>Note type
                <select id={uid + '-category'} className="pm-input" value={category} disabled={save.isPending}
                    onChange={e => setCategory(e.target.value as NoteSection)}>
                    {Object.entries(NOTE_SECTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
            </label>}
            <label className="pm-label" htmlFor={uid}> {replyTo ? 'Clarification' : 'New note'}</label>
            <textarea id={uid} className="pm-input min-h-24" value={draft} disabled={save.isPending}
                onChange={e => {setRequestId(requestId);setDraft(e.target.value);}} maxLength={1800} autoFocus />
            {encoded.length > 2000 && <p role="alert">This note is too long. Shorten it before saving.</p>}
            {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
            <div className="flex gap-2">
                <button type="submit" className="pm-btn-primary text-xs" disabled={save.isPending || !draft.trim() || encoded.length > 2000}>{save.isPending ? 'Saving…' : 'Save new note'}</button>
                <button type="button" className="pm-btn-ghost text-xs" disabled={save.isPending}
                    onClick={() => { setOpen(false); setReplyTo(null); }}>Cancel</button>
            </div>
        </form>}
    </section>;
}
